import { Box } from "@mui/material";
import type { FC } from "react";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { CORNER_RIBBON, SHADOW } from "@/theme/tokens";

const COMPACT = CORNER_RIBBON.COMPACT;

/** `solid` is the brand band; `dashed` is a white band with a dashed brand edge, for something not in effect yet. */
export type CornerRibbonVariant = "solid" | "dashed";

type Props = {
	/** The short word on the band, already translated ("Holiday"). */
	label: string;
	/** Hover and screen reader text, already translated ("Christmas · Group A"). */
	tooltip: string;
	variant: CornerRibbonVariant;
	/** Inside a control whose own label already says what the ribbon says: no tab stop and hidden from screen readers. */
	// DEFAULT: false, a ribbon that is the only place its text appears.
	decorative?: boolean;
	/** A thin band without the word, for a narrow cell. The word stays in the hover and screen reader text. */
	// DEFAULT: false, the full band with its word.
	compact?: boolean;
	dataTestId: string;
};

/**
 * A diagonal band across the top end corner of its parent, which must be `position: relative`. The corner box
 * clips the band's ends, and the band mirrors to the top-left corner in right-to-left languages.
 */
export const CornerRibbon: FC<Props> = ({ label, tooltip, variant, decorative = false, compact = false, dataTestId }) => (
	<Box
		sx={{
			position: "absolute",
			top: 0,
			insetInlineEnd: 0,
			width: compact ? COMPACT.CORNER_PX : CORNER_RIBBON.CORNER_PX,
			height: compact ? COMPACT.CORNER_PX : CORNER_RIBBON.CORNER_PX,
			overflow: "hidden",
			pointerEvents: "none",
		}}
	>
		<InHouseTooltip title={tooltip}>
			<Box
				component="span"
				role={decorative ? undefined : "img"}
				aria-label={decorative ? undefined : tooltip}
				aria-hidden={decorative}
				tabIndex={decorative ? undefined : 0}
				data-testid={dataTestId}
				data-variant={variant}
				sx={(theme) => ({
					position: "absolute",
					boxSizing: "border-box",
					...(compact
						? { top: COMPACT.BAND_TOP_PX, insetInlineEnd: -COMPACT.BAND_OVERHANG_PX, width: COMPACT.BAND_WIDTH_PX, height: COMPACT.BAND_HEIGHT_PX }
						: {
								top: CORNER_RIBBON.BAND_TOP_PX,
								insetInlineEnd: -CORNER_RIBBON.BAND_OVERHANG_PX,
								width: CORNER_RIBBON.BAND_WIDTH_PX,
								py: `${CORNER_RIBBON.BAND_PADDING_Y_PX}px`,
							}),
					transform: `rotate(${CORNER_RIBBON.ANGLE_DEG}deg)`,
					"[dir='rtl'] &": { transform: `rotate(-${CORNER_RIBBON.ANGLE_DEG}deg)` },
					pointerEvents: "auto",
					textAlign: "center",
					fontSize: CORNER_RIBBON.FONT_SIZE,
					fontWeight: CORNER_RIBBON.FONT_WEIGHT,
					letterSpacing: CORNER_RIBBON.LETTER_SPACING,
					lineHeight: CORNER_RIBBON.LINE_HEIGHT,
					textTransform: "uppercase",
					cursor: "default",
					backgroundColor: variant === "dashed" ? theme.palette.background.paper : theme.palette.primary.main,
					color: variant === "dashed" ? theme.palette.primary.main : theme.palette.primary.contrastText,
					// The solid band keeps a transparent edge of the same width, so both variants are the same size.
					border: `${CORNER_RIBBON.BORDER_PX}px ${variant} ${variant === "dashed" ? theme.palette.primary.main : "transparent"}`,
					boxShadow: variant === "dashed" ? "none" : SHADOW.CONTROL_RAISED,
					"&:focus-visible": {
						outline: `${CORNER_RIBBON.FOCUS_RING_PX}px solid ${theme.palette.primary.main}`,
						outlineOffset: CORNER_RIBBON.FOCUS_RING_OFFSET_PX,
					},
				})}
			>
				{compact ? null : label}
			</Box>
		</InHouseTooltip>
	</Box>
);
